import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useState, useEffect, Suspense } from "react";
import { HelmetProvider } from "react-helmet-async";
import React from "react";
import { FaDownload, FaTimes } from "react-icons/fa";

// Lazy load components
const Index = React.lazy(() => import("./pages/Home"));
const About = React.lazy(() => import("./pages/About"));
const Plans = React.lazy(() => import("./pages/Plans"));
const Document = React.lazy(() => import("./pages/Document"));
const Team = React.lazy(() => import("./pages/Team"));
const HowItWorks = React.lazy(() => import("./pages/HowItWorks"));
const Sustainability = React.lazy(() => import("./pages/Sustainability"));
const FAQ = React.lazy(() => import("./pages/FAQ"));
const Contact = React.lazy(() => import("./pages/Contact"));
const Login = React.lazy(() => import("./pages/Login"));
const Register = React.lazy(() => import("./pages/Register"));
const TokenPocketPage = React.lazy(() => import("./pages/wallets/TokenPocket"));
const TrustWalletPage = React.lazy(() => import("./pages/wallets/TrustWallet"));
const PhantomPage = React.lazy(() => import("./pages/wallets/Phantom"));
const CryptoWalletPage = React.lazy(() => import("./pages/wallets/CryptoWallet"));
const CustomerDashboard = React.lazy(() => import("./pages/dashboard/CustomerDashboard"));
const AdminDashboard = React.lazy(() => import("./pages/dashboard/AdminDashboard"));
const Navbar = React.lazy(() => import("./components/layout/Navbar"));
const RoiCalculator = React.lazy(() => import("./components/RoiCalculator"));

// Import AppDownload from pages
const AppDownload = React.lazy(() => import("./pages/AppDownload"));

// Import TelegramQR
const TelegramQR = React.lazy(() => import("./pages/TelegramQR"));

// Policy pages
const Terms = React.lazy(() => import("./pages/Terms"));
const Privacy = React.lazy(() => import("./pages/Privacy"));
const Refund = React.lazy(() => import("./pages/Refund"));

// Loading component
const LoadingSpinner = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="text-center">
      <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
      <p className="text-muted-foreground">Loading Dream Hub...</p>
    </div>
  </div>
);

// Main App Content Component
const AppContent = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const location = useLocation();

  // Check if current path is a dashboard path
  const isDashboardPath = location.pathname.startsWith('/dashboard') || 
                         location.pathname.startsWith('/admin');
  const hasAutoLoginSession = (() => {
    try {
      return !!sessionStorage.getItem('dreamhub_auto_user');
    } catch (error) {
      return false;
    }
  })();
  const isAutoLoginRequested =
    new URLSearchParams(location.search).get('autoLogin') === 'true' || hasAutoLoginSession;

  // Check if current path is a public page (not dashboard/admin)
  const isPublicPage = !isDashboardPath;
  const isAuthPage = location.pathname === '/login' || location.pathname === '/register';

  useEffect(() => {
    const savedUser = localStorage.getItem("dreamhub_user");
    if (savedUser) {
      try {
        const parsedUser = JSON.parse(savedUser);
        if ((parsedUser.sponsorId && parsedUser.isAuthenticated) || parsedUser.role === 'admin') {
          setUser(parsedUser);
        }
      } catch (error) {
        localStorage.removeItem("dreamhub_user");
      }
    }
    setLoading(false);
  }, []);

  // AppDownload Floating Button
  const FloatingDownloadButton = () => {
    // Show ONLY on public pages (hide on dashboard/admin pages)
    if (!isPublicPage) return null;
    
    return (
      <button
        onClick={() => setShowDownloadModal(true)}
        className="fixed bottom-6 right-6 z-40 bg-gradient-to-r from-blue-600 to-purple-600 text-white p-4 rounded-full shadow-2xl hover:shadow-3xl hover:scale-110 transition-all duration-300"
        aria-label="Download App"
      >
        <FaDownload className="w-6 h-6" />
      </button>
    );
  };

  const handleLogin = async (sponsorId, role = "customer") => {
    try {
      if (role === "admin") {
        const adminData = {
          sponsorId: 'ADMIN001',
          name: 'Administrator',
          email: 'admin@dreamhub.com',
          role: 'admin',
          lastLogin: new Date().toISOString(),
          isAuthenticated: true,
          status: 'active'
        };
        setUser(adminData);
        localStorage.setItem("dreamhub_user", JSON.stringify(adminData));
        return { success: true, role: 'admin' };
      } else {
        const tempUser = {
          sponsorId: sponsorId.toUpperCase(),
          role: 'customer',
          isAuthenticated: true,
          validating: true
        };
        setUser(tempUser);
        localStorage.setItem("dreamhub_user", JSON.stringify(tempUser));
        return { success: true, role: 'customer' };
      }
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const handleLogout = () => {
    setUser(null);
    localStorage.removeItem("dreamhub_user");
    localStorage.removeItem("dreamhub_referral");
    localStorage.removeItem("dreamhub_admin");
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground">
      {/* Floating Download Button - Show on ALL public pages (regardless of login) */}
      <FloatingDownloadButton />
      
      {/* Download Modal - Show on ALL public pages */}
      {isPublicPage && showDownloadModal && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
          <Suspense fallback={<LoadingSpinner />}>
            <div className="relative">
              <button
                onClick={() => setShowDownloadModal(false)}
                className="absolute -top-10 right-0 text-white hover:text-gray-300 text-xl z-10"
              >
                <FaTimes />
              </button>
              <AppDownload />
            </div>
          </Suspense>
        </div>
      )}

      {/* TelegramQR - Show on ALL public pages (regardless of login) */}
      {isPublicPage && <Suspense fallback={null}><TelegramQR /></Suspense>}

      {/* ROI Calculator - Show only on public pages (hide on login/register) */}
      {isPublicPage && !isAuthPage && (
        <Suspense fallback={null}>
          <RoiCalculator />
        </Suspense>
      )}

      {/* Navbar - Show on ALL public pages (regardless of login) */}
      {isPublicPage && <Suspense fallback={null}><Navbar /></Suspense>}

      <main className="flex-1">
        <Routes>
          {/* Public Routes - TelegramQR & AppDownload WILL appear here */}
          <Route path="/" element={<Index />} />
          <Route path="/about" element={<About />} />
          <Route path="/plans" element={<Plans />} />
          <Route path="/how-it-works" element={<HowItWorks />} />
          <Route path="/sustainability" element={<Sustainability />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/refund" element={<Refund />} />
          <Route path="/document" element={<Document />} />
          <Route path="/team" element={<Team />} />
          <Route path="/wallet/token-pocket" element={<TokenPocketPage />} />
          <Route path="/wallet/trust-wallet" element={<TrustWalletPage />} />
          <Route path="/wallet/phantom" element={<PhantomPage />} />
          <Route path="/wallet/crypto-wallet" element={<CryptoWalletPage />} />
          
          {/* Login/Register - Still public pages (TelegramQR & AppDownload WILL appear) */}
          <Route 
            path="/register" 
            element={
              user ? (
                user.role === "admin" ? 
                  <Navigate to="/admin/dashboard" /> : 
                  <Navigate to="/dashboard" />
              ) : (
                <Register />
              )
            } 
          />
          
          <Route 
            path="/login" 
            element={
              user ? (
                user.role === "admin" ? 
                  <Navigate to="/admin/dashboard" /> : 
                  <Navigate to="/dashboard" />
              ) : (
                <Login onLogin={handleLogin} />
              )
            } 
          />

          {/* Dashboard Routes - TelegramQR & AppDownload WON'T appear here */}
          <Route
            path="/dashboard/*"
            element={
              (user || isAutoLoginRequested) ? (
                <CustomerDashboard user={user} onLogout={handleLogout} />
              ) : (
                <Navigate to="/login" />
              )
            }
          />

          {/* Admin Routes - TelegramQR & AppDownload WON'T appear here */}
          <Route
            path="/admin/*"
            element={
              user?.role === 'admin' ? (
                <AdminDashboard user={user} onLogout={handleLogout} />
              ) : (
                <Navigate to="/login" />
              )
            }
          />

          <Route 
            path="*" 
            element={
              user ? (
                user.role === "admin" ? 
                  <Navigate to="/admin/dashboard" /> : 
                  <Navigate to="/dashboard" />
              ) : (
                <Navigate to="/" />
              )
            } 
          />
        </Routes>
      </main>
    </div>
  );
};

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <Suspense fallback={<LoadingSpinner />}>
          <AppContent />
        </Suspense>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
